import React from "react";

import { useTranslation } from "../../i18n/TranslationProvider";
import { translateOr } from "../../i18n/translateData";


/**
 * ExerciseChangeAlert
 *
 * Shown on the provider dashboard when a selected patient has at least one
 * assigned exercise where:
 *   - progress > 0  (positive/green progress)
 *   - OR level > previousLevel  (levelled up since last evaluation)
 *
 * Props:
 *   exercisesByCategory  {object}   — the exercisesByCategory map built in ProviderAssignedExercises
 *   dismissed            {boolean}  — whether the provider has closed the alert
 *   onDismiss            {function} — called when the × button is clicked
 *   onOpenUpdateModal     {function} — called when the provider clicks "Update Exercises" from the capped alert
 *   patientName          {string}   — display name of the selected patient
 */
const ExerciseChangeAlert = ({ exercisesByCategory, dismissed, onDismiss, onOpenUpdateModal, patientName }) => {
    const { t } = useTranslation();
    if (dismissed) return null;

    const exercisesWithChanges = Object.values(exercisesByCategory)
        .flatMap(cat => cat.exercises)
        .filter(ex => {
            const progressIncreased = ex.progress > 0;
            const leveledUp =
                ex.previousLevel !== null &&
                ex.previousLevel !== undefined &&
                ex.level > ex.previousLevel;
            return progressIncreased || leveledUp;
        });

    if (exercisesWithChanges.length === 0) return null;

    const today = new Date().toISOString().split('T')[0];

    const isDailyLimitReached = (ex) =>
        ex.numLogs >= 2 && ex.dateLastLogged?.startsWith(today);

    return (
        <div style={{
            backgroundColor: '#d4edda',
            border: '2px solid #28a745',
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '10px',
            color: '#155724',
            position: 'relative'
        }}>
            <button
                onClick={onDismiss}
                style={{
                    position: 'absolute',
                    top: '8px',
                    right: '10px',
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#155724',
                    cursor: 'pointer',
                    lineHeight: 1
                }}
                aria-label={t("dashboard.ui.dismiss")}
            >
                ×
            </button>
            <strong style={{ fontSize: '1.25rem', fontWeight: '500' }}>
                {t("dashboard.exerciseChangeAlert.title")}
            </strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {exercisesWithChanges.map((ex, i) => (
                    <li key={i} style={{ fontSize: '13px', marginBottom: '8px' }}>

                        {/* Exercise Name */}
                        <strong>{translateOr(t, `dashboard.exerciseNames.${ex.name}`, ex.name)}</strong>

                        {/* Current Level */}
                        {ex.level !== null && ex.level !== undefined && (
                            <span style={{
                                display: 'inline-block',
                                marginLeft: '8px',
                                padding: '2px 10px',
                                backgroundColor: '#28a745',
                                color: '#fff',
                                borderRadius: '12px',
                                fontSize: '13px',
                                fontWeight: '700',
                                verticalAlign: 'middle',
                                letterSpacing: '0.02em'
                            }}>
                                {t("dashboard.exerciseChangeAlert.level")} {ex.level}
                            </span>
                        )}

                        {/* Daily Limit Alert - mirrors patient dashboard */}
                        {isDailyLimitReached(ex) && (
                            <div style={{
                                fontSize: '11px',
                                color: '#dc3545',
                                fontWeight: '600',
                                marginTop: '4px',
                                marginBottom: '2px',
                                padding: '4px 8px',
                                backgroundColor: 'rgba(220, 53, 69, 0.05)',
                                borderRadius: '4px',
                                border: '1px solid rgba(220, 53, 69, 0.2)'
                            }}>
                                {t("dashboard.exerciseChangeAlert.dailyLimitReached")}
                            </div>
                        )}

                        {ex.progress >= 0 && (
                            <div style={{ marginTop: '6px' }}>
                                <span style={{ display: 'block', marginBottom: '4px' }}>
                                    {t("dashboard.exerciseChangeAlert.progress")}{ex.progress}/5
                                </span>
                                {/* Visual progress bar (mirrors patient dashboard) */}
                                <div style={{
                                    display: 'flex',
                                    height: '8px',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    backgroundColor: '#b8dfc4',
                                    maxWidth: '160px'
                                }}>
                                    {[1, 2, 3, 4, 5].map(value => (
                                        <div
                                            key={value}
                                            style={{
                                                flex: 1,
                                                backgroundColor: ex.progress >= value ? '#28a745' : 'transparent',
                                                marginRight: value < 5 ? '2px' : '0',
                                                transition: 'background-color 0.3s ease'
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {ex.previousLevel !== null && ex.previousLevel !== undefined && ex.level > ex.previousLevel && (
                            <div style={{
                                fontSize: '12px',
                                color: '#155724',
                                fontWeight: '600',
                                marginTop: '4px'
                            }}>
                                {ex.progress === 5 && ex.level >= 10
                                    ? t("dashboard.exerciseChangeAlert.levelMaxReached")
                                    : ex.progress === 5
                                    ? <>
                                        {t("dashboard.exerciseChangeAlert.levelCapped", { next: ex.level + 1 })}
                                        {onOpenUpdateModal && (
                                            <button
                                                onClick={onOpenUpdateModal}
                                                style={{
                                                    display: 'block',
                                                    marginTop: '6px',
                                                    padding: '5px 14px',
                                                    backgroundColor: '#6f42c1',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                    fontWeight: '700',
                                                    cursor: 'pointer',
                                                    letterSpacing: '0.02em'
                                                }}
                                            >
                                                → {t("dashboard.updateExercisesButton")}
                                            </button>
                                        )}
                                      </>
                                    : t("dashboard.exerciseChangeAlert.levelChange", { prev: ex.previousLevel, next: ex.level })}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default ExerciseChangeAlert;